/**
 * A anamnese digital vista da recepção: a situação (sem o conteúdo, que é do médico) e os dois
 * jeitos de colher — abrir agora num tablet para o trabalhador responder, ou mandar o link.
 *
 * "Preencher no tablet" gera um link novo de prazo curto e abre numa aba; o link anterior deixa
 * de valer, porque só o hash fica guardado.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Send as SendIcon, TabletAndroid as TabletIcon } from '@mui/icons-material';

import anamneseDigitalService, { type SituacaoDaAnamnese } from '../../services/anamneseDigitalService';

const ROTULO: Record<SituacaoDaAnamnese['status'], { texto: string; cor: 'default' | 'warning' | 'success' }> = {
  nao_enviada: { texto: 'Não enviada', cor: 'default' },
  pendente: { texto: 'Aguardando resposta', cor: 'warning' },
  respondida: { texto: 'Respondida e assinada', cor: 'success' },
  cancelada: { texto: 'Cancelada', cor: 'default' },
};

const CANAL: Record<string, string> = { whatsapp: 'WhatsApp', email: 'e-mail' };

function quando(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('pt-BR') : '';
}

export interface AnamneseDaRecepcaoDialogProps {
  appointmentId: number | string | null;
  paciente?: string;
  onClose: () => void;
}

export const AnamneseDaRecepcaoDialog: React.FC<AnamneseDaRecepcaoDialogProps> = ({ appointmentId, paciente, onClose }) => {
  const [situacao, setSituacao] = useState<SituacaoDaAnamnese | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    if (appointmentId === null) return;
    setErro(null);
    try {
      setSituacao(await anamneseDigitalService.situacao(appointmentId));
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível ver a anamnese.');
    }
  }, [appointmentId]);

  useEffect(() => {
    setSituacao(null);
    setAviso(null);
    void carregar();
  }, [carregar]);

  const preencherNoTablet = useCallback(async () => {
    if (appointmentId === null) return;
    // A aba abre já no clique (o navegador bloqueia janela aberta depois de um await).
    const aba = window.open('', '_blank');
    setOcupado(true);
    setErro(null);
    try {
      const { url } = await anamneseDigitalService.gerarLink(appointmentId, false);
      if (aba) aba.location.href = `${url}?tablet=1`;
      else window.location.assign(`${url}?tablet=1`);
      await carregar();
    } catch (err: unknown) {
      aba?.close();
      setErro(err instanceof Error ? err.message : 'Não foi possível abrir o questionário.');
    } finally {
      setOcupado(false);
    }
  }, [appointmentId, carregar]);

  const enviarLink = useCallback(async () => {
    if (appointmentId === null) return;
    setOcupado(true);
    setErro(null);
    try {
      const { enviadoPor } = await anamneseDigitalService.gerarLink(appointmentId, true);
      setAviso(
        enviadoPor
          ? `Link enviado por ${CANAL[enviadoPor] ?? enviadoPor}.`
          : 'O funcionário não tem WhatsApp nem e-mail cadastrado (ou o canal não está configurado). Use o tablet.'
      );
      await carregar();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível enviar o link.');
    } finally {
      setOcupado(false);
    }
  }, [appointmentId, carregar]);

  const respondida = situacao?.status === 'respondida';

  return (
    <Dialog open={appointmentId !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Anamnese{paciente ? ` — ${paciente}` : ''}</DialogTitle>
      <DialogContent>
        {!situacao && !erro && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {situacao && (
          <Stack spacing={1.5}>
            <Chip sx={{ alignSelf: 'flex-start' }} color={ROTULO[situacao.status].cor} label={ROTULO[situacao.status].texto} />
            {situacao.enviadaEm && (
              <Typography variant="body2" color="text.secondary">
                Link enviado em {quando(situacao.enviadaEm)}
                {situacao.canalDoEnvio ? ` por ${CANAL[situacao.canalDoEnvio] ?? situacao.canalDoEnvio}` : ''}.
              </Typography>
            )}
            {respondida && (
              <Typography variant="body2" color="text.secondary">
                Respondida em {quando(situacao.respondidaEm)}
                {situacao.origemDaResposta === 'tablet' ? ', no tablet da recepção' : ', pelo celular'}. O médico vê as
                respostas na consulta.
              </Typography>
            )}
            {!respondida && (
              <Typography variant="body2" color="text.secondary">
                O trabalhador responde e assina sozinho. Se ele ainda não respondeu pelo celular, abra o questionário no
                tablet e entregue a ele.
              </Typography>
            )}
          </Stack>
        )}
        {aviso && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {aviso}
          </Alert>
        )}
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        {situacao && !respondida && (
          <>
            <Button startIcon={<SendIcon />} onClick={() => void enviarLink()} disabled={ocupado}>
              Enviar link
            </Button>
            <Button variant="contained" startIcon={<TabletIcon />} onClick={() => void preencherNoTablet()} disabled={ocupado}>
              Preencher no tablet
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnamneseDaRecepcaoDialog;
